import React, {useEffect, useRef} from "react"
import {BrandData, OscarBooking, OscarConfigModel} from "@/lib/oscar/ApiModels.ts";
import ErrorOverlay from "@/components/overlay/ErrorOverlay.tsx";
import {LoadOverlay} from "@/components/overlay/LoadOverlay.tsx";
import Api from "@/lib/oscar/Api.ts";
import {FullScreenLoading} from "@/components/overlay/FullScreenLoading.tsx";
import {BookingKind} from "@/lib/enum/BookingKind.ts";

export type OscarConfig = {
    brand: string;
    showHeader: boolean;
    communityBooking: boolean;
    calendarCount: number;
    tightMode?: boolean;
    defaultBinKind: BookingKind;
}

export type OscarContextType = {
    authToken: string;
    appConfig: OscarConfigModel|null;
    brandConfig: BrandData|null;
    localConfig: OscarConfig;

    finalBooking: OscarBooking|null;
    setLoading: (status: boolean) => void;
    setBooking: (booking: OscarBooking) => void;
}

export const OscarContext = React.createContext<OscarContextType | null>(null);

const OscarProvider: React.FC<{children: React.ReactNode, localConfig: OscarConfig}> = ({ children, localConfig }) => {
    // External state
    const [brand] = React.useState<string>(localConfig.brand ?? 'act_skip_hire');
    const [appConfig, setAppConfig] = React.useState<OscarConfigModel|null>(null);
    const [brandConfig, setBrandConfig] = React.useState<BrandData|null>(null);
    const [authToken, setAuthToken] = React.useState<string>('');

    // Internal state
    const [appError, setAppError] = React.useState<string|null>(null);
    const [appLoading, setAppLoading] = React.useState<boolean>(true);
    const didInit = useRef(false); // Add a ref to track if fetch has already been called
    const [loadingLockout, setLoadingLockout] = React.useState<boolean>(false);
    const [finalBooking, setFinalBooking] = React.useState<OscarBooking|null>(null);

    /**
     * Register the session and fetch the app config.
     */
    useEffect(() => {
        if (didInit.current) return; // Prevent re-fetch
        didInit.current = true; // Mark as fetched

        const registerSession = async () => {
            try {
                const data = await Api.register(brand);

                setAppConfig(data.config);
                setAuthToken(data.token);
                setBrandConfig(getBrandData(brand, data.config) ?? null);
                setAppLoading(false);
            } catch (e) {
                setAppError((e as Error).message);
                setAppLoading(false);
            }
        };

        registerSession();
    }, [brand]);

    /**
     * Set the loading state.
     * @param status
     */
    const setLoading = (status: boolean) => {
        setLoadingLockout(status);
    };

    /**
     * Set the booking data.
     * @param booking
     */
    const setBooking = (booking: OscarBooking) => {
        setFinalBooking(booking);
        setLoadingLockout(false);
    }

    return (
        <OscarContext.Provider value={{authToken, localConfig, appConfig, brandConfig, setBooking, setLoading, finalBooking}}>
            {loadingLockout && <FullScreenLoading />}
            {appError != null && <ErrorOverlay errorMessage={appError} />}
            {appLoading && <LoadOverlay />}
            {appError == null && !appLoading && children}
        </OscarContext.Provider>
    );
}


/// Get the brand data
function getBrandData(brand: string, config: OscarConfigModel) {
    // Check for V2 brand data
    if (config.brandData !== null) {
        return config.brandData;
    }

    if (brand === 'angel_waste') {
        return config?.brandHolderData?.angelWaste
    } else if (brand === 'cut_price_skips') {
        return config?.brandHolderData?.cutPrice
    } else if (brand === 'capital_hire') {
        return config?.brandHolderData?.capitalHire
    }

    return config?.brandHolderData?.actSkipHire
}

export default OscarProvider;